import { ArrowRight, ArrowUpRight, ArrowDown, Check, Zap, Megaphone, Mail, Smartphone, BarChart3, Users, TrendingUp, Shield, Clock, Globe, Building2, ShoppingBag, GraduationCap, Heart, Plane, Calendar, CreditCard, Radio, Box } from 'lucide-react';
import SEO from '@/components/SEO';
import { site } from '@/data/content';

export default function Moments() {
  const momentsFaqs = [
    {
      q: 'What is ZuriHub Moments?',
      a: 'ZuriHub Moments is a multi-channel marketing automation platform that lets you send WhatsApp Marketing, email campaigns, and bulk SMS messages from one unified dashboard.',
    },
    {
      q: 'How does the pay-as-you-go pricing work?',
      a: 'You only pay for what you send. No monthly subscriptions or hidden fees. Simply top up your balance and send messages at competitive per-message rates.',
    },
    {
      q: 'Can I schedule campaigns in advance?',
      a: 'Yes. Schedule your WhatsApp Marketing, email campaigns, and SMS messages to be sent at the perfect time for your audience.',
    },
    {
      q: 'Do I need to register sender IDs for SMS?',
      a: 'Yes. For professional SMS delivery, you can register your business sender ID in your Moments dashboard. We guide you through the simple process.',
    },
    {
      q: 'What WhatsApp template categories are supported?',
      a: 'We support all Meta template categories: Marketing (promotions, offers), Utility (order updates, account alerts), and Authentication (OTP, verification codes).',
    },
    {
      q: 'Can I track campaign performance?',
      a: 'Absolutely. View real-time analytics for delivery rates, open rates, click rates, and engagement across all channels from one dashboard.',
    },
  ];

  const industries = [
    {
      name: 'E-commerce',
      icon: ShoppingBag,
      description: 'Recover 25% more abandoned carts with WhatsApp Marketing reminders. Send order updates, delivery notifications, and promotional offers.',
      stat: '25% cart recovery',
    },
    {
      name: 'Education',
      icon: GraduationCap,
      description: 'Increase course enrollment by 40% with targeted WhatsApp and SMS campaigns. Share exam reminders and study materials.',
      stat: '40% higher enrollment',
    },
    {
      name: 'Healthcare',
      icon: Heart,
      description: 'Reduce no-shows by 60% with automated appointment reminders via SMS. Send health tips and follow-up messages.',
      stat: '60% fewer no-shows',
    },
    {
      name: 'Travel & Hospitality',
      icon: Plane,
      description: 'Boost booking confirmations with 98% delivery rates via SMS. Share travel updates and promotional offers instantly.',
      stat: '98% delivery rate',
    },
    {
      name: 'Real Estate',
      icon: Building2,
      description: 'Generate 3x more leads with WhatsApp property alerts. Send open house invitations and market updates.',
      stat: '3x more leads',
    },
    {
      name: 'Financial Services',
      icon: CreditCard,
      description: 'Improve customer engagement by 50% with secure transaction alerts via SMS and email. Send OTP and verification codes.',
      stat: '50% higher engagement',
    },
  ];

  const useCases = [
    {
      title: 'Flash Sales & Promotions',
      description: 'Generate 3x more conversions with time-limited offers sent via WhatsApp Marketing. Create urgency and drive immediate action.',
      icon: Zap,
      result: '3x more conversions',
    },
    {
      title: 'Customer Re-engagement',
      description: 'Win back 40% of inactive customers with personalized email campaigns and WhatsApp messages. Automate follow-ups at scale.',
      icon: Users,
      result: '40% re-engagement rate',
    },
    {
      title: 'Event Invitations',
      description: 'Achieve 70% attendance rates with automated email invitations and SMS reminders. Send updates and confirmations instantly.',
      icon: Calendar,
      result: '70% attendance rate',
    },
    {
      title: 'Order Notifications',
      description: 'Reduce support tickets by 50% with automated order confirmations, shipping updates, and delivery notifications via WhatsApp and SMS.',
      icon: Box,
      result: '50% fewer support tickets',
    },
    {
      title: 'Lead Nurturing',
      description: 'Close 35% more deals with targeted email sequences that nurture leads through your sales funnel. Personalize at scale.',
      icon: TrendingUp,
      result: '35% higher close rate',
    },
    {
      title: 'Survey & Feedback',
      description: 'Get 5x more responses with SMS surveys and email campaigns. Collect real-time feedback to improve your services.',
      icon: Radio,
      result: '5x more responses',
    },
  ];

  const pricing = [
    {
      channel: 'WhatsApp Marketing',
      icon: Megaphone,
      description: 'Official Meta-approved templates with high delivery rates',
      startingFrom: 'WhatsApp Business API rates apply',
      features: [
        'Template approval workflow',
        'Quality rating tracking',
        'Messaging limit monitoring',
        'Rich media support',
        'Button interactions',
        'Carousel messages',
      ],
    },
    {
      channel: 'Email Campaigns',
      icon: Mail,
      description: 'Professional email marketing with automation',
      startingFrom: 'Pay per email sent',
      features: [
        'Beautiful templates',
        'A/B testing',
        'Automation workflows',
        'Segmentation',
        'Analytics dashboard',
        'SPF/DKIM authentication',
      ],
    },
    {
      channel: 'Bulk SMS',
      icon: Smartphone,
      description: 'Global SMS delivery with sender ID registration',
      startingFrom: 'Competitive per-SMS rates',
      features: [
        'Sender ID registration',
        'Delivery receipts',
        'Scheduling',
        'Contact management',
        'Opt-out management',
        'Compliance tools',
      ],
    },
  ];

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'ZuriHub Moments - Multi-Channel Marketing Automation',
      url: `${site.baseUrl}/moments`,
      description: 'Send WhatsApp Marketing, email campaigns, and bulk SMS from one platform. Pay-as-you-go pricing with no monthly fees.',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'ZuriHub Moments',
      description: 'Multi-channel marketing automation platform for WhatsApp Marketing, email campaigns, and bulk SMS.',
      brand: { '@type': 'Brand', name: site.name },
      offers: {
        '@type': 'Offer',
        priceCurrency: 'USD',
        priceSpecification: {
          '@type': 'PriceSpecification',
          priceComponent: [
            {
              '@type': 'UnitPriceSpecification',
              name: 'WhatsApp Messages',
              description: 'Pay-as-you-go pricing based on Meta Business API rates',
            },
            {
              '@type': 'UnitPriceSpecification',
              name: 'Email Campaigns',
              description: 'Pay per email sent with volume discounts',
            },
            {
              '@type': 'UnitPriceSpecification',
              name: 'Bulk SMS',
              description: 'Competitive per-SMS rates with global coverage',
            },
          ],
        },
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: momentsFaqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: site.name,
      url: site.baseUrl,
      sameAs: [site.twitter, site.linkedin],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: site.name,
      description: 'Multi-channel marketing automation platform',
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'UG',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 0.3476,
        longitude: 32.5825,
      },
    },
  ];

  return (
    <>
      <SEO
        title="ZuriHub Moments - WhatsApp Marketing, Email & SMS Marketing"
        description="Send WhatsApp Marketing, email campaigns, and bulk SMS from one platform. Pay-as-you-go pricing with no monthly fees. Reach customers across multiple channels."
        path="/moments"
        jsonLd={jsonLd}
      >
        <meta
          name="keywords"
          content="WhatsApp Marketing, email marketing, bulk SMS, marketing automation, WhatsApp Business API, SMS campaigns, email newsletters, multi-channel marketing, pay-as-you-go messaging, customer engagement"
        />
        <meta name="geo.region" content="UG" />
        <meta name="geo.placename" content="Uganda" />
        <meta name="geo.position" content="0.3476;32.5825" />
        <meta name="ICBM" content="0.3476, 32.5825" />
      </SEO>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-32 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-500/10 px-4 py-1.5 text-sm font-semibold text-brand-300 backdrop-blur">
                <Zap className="h-4 w-4" /> Multi-Channel Marketing
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                Reach customers where they are with <span className="gradient-text">Moments</span>
              </h1>
              <p className="mt-6 text-lg text-slate-300">
                Send WhatsApp Marketing, email campaigns, and bulk SMS from one unified dashboard. 
                Pay only for what you send. No monthly subscriptions.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-600 px-6 py-3 font-semibold text-white hover:bg-brand-500 transition-colors"
                >
                  Start Free
                  <ArrowRight className="h-4 w-4" />
                </a>
                <a
                  href="#pricing"
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-600 px-6 py-3 font-semibold text-white hover:bg-slate-800 transition-colors"
                >
                  View Pricing
                  <ArrowDown className="h-4 w-4" />
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-tr from-brand-500/20 to-purple-500/20 blur-3xl" />
              <div className="relative rounded-2xl bg-slate-900/50 border border-slate-800 p-8 backdrop-blur">
                <img 
                  src="https://media.getzurihub.com/Jayrah%20Assets/WaBa.png" 
                  alt="WhatsApp Marketing" 
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Channels Section */}
      <section className="bg-white px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl">
              Three powerful channels. One platform.
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Choose the right channel for every message. Or use all three for maximum impact.
            </p>
          </div>

          <div className="mt-16 grid gap-8 md:grid-cols-3">
            {/* WhatsApp */}
            <a href="/channels/whatsapp" className="rounded-2xl border border-slate-200 p-8 hover:border-green-500 transition-colors block">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-green-100">
                <Megaphone className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="mt-4 text-xl font-bold">WhatsApp Marketing</h3>
              <p className="mt-2 text-slate-600">
                Send promotional messages, order updates, and authentication codes via WhatsApp Business API.
              </p>
              <ul className="mt-6 space-y-3">
                <li className="flex items-start gap-2 text-sm text-slate-600">
                  <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
                  Meta-approved templates
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-600">
                  <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
                  Rich media & buttons
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-600">
                  <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
                  Quality rating tracking
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-600">
                  <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
                  Messaging limit monitoring
                </li>
              </ul>
              <div className="mt-6 pt-4 border-t border-slate-100">
                <span className="text-sm font-semibold text-green-600 flex items-center gap-1">
                  Learn more <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </a>

            {/* Email */}
            <a href="/email-marketing" className="rounded-2xl border border-slate-200 p-8 hover:border-blue-500 transition-colors block">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100">
                <Mail className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="mt-4 text-xl font-bold">Email Campaigns</h3>
              <p className="mt-2 text-slate-600">
                Create beautiful email newsletters, automated sequences, and targeted campaigns.
              </p>
              <ul className="mt-6 space-y-3">
                <li className="flex items-start gap-2 text-sm text-slate-600">
                  <Check className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  Drag-and-drop builder
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-600">
                  <Check className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  A/B testing
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-600">
                  <Check className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  Automation workflows
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-600">
                  <Check className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  Detailed analytics
                </li>
              </ul>
              <div className="mt-6 pt-4 border-t border-slate-100">
                <span className="text-sm font-semibold text-blue-600 flex items-center gap-1">
                  Learn more <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </a>

            {/* SMS */}
            <a href="/bulk-messaging" className="rounded-2xl border border-slate-200 p-8 hover:border-purple-500 transition-colors block">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-purple-100">
                <Smartphone className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="mt-4 text-xl font-bold">Bulk SMS</h3>
              <p className="mt-2 text-slate-600">
                Send bulk SMS messages globally with professional sender ID registration.
              </p>
              <ul className="mt-6 space-y-3">
                <li className="flex items-start gap-2 text-sm text-slate-600">
                  <Check className="h-5 w-5 text-purple-600 flex-shrink-0" />
                  Global coverage
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-600">
                  <Check className="h-5 w-5 text-purple-600 flex-shrink-0" />
                  Sender ID registration
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-600">
                  <Check className="h-5 w-5 text-purple-600 flex-shrink-0" />
                  Delivery receipts
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-600">
                  <Check className="h-5 w-5 text-purple-600 flex-shrink-0" />
                  Scheduling
                </li>
              </ul>
              <div className="mt-6 pt-4 border-t border-slate-100">
                <span className="text-sm font-semibold text-purple-600 flex items-center gap-1">
                  Learn more <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="bg-slate-50 px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl">
              Drive results with proven use cases
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              From flash sales to customer re-engagement, Moments helps you achieve measurable marketing goals with data-backed results.
            </p>
          </div>

          <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {useCases.map((useCase, index) => (
              <div key={index} className="rounded-2xl bg-white p-6 shadow-sm border border-slate-200">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-100">
                  <useCase.icon className="h-5 w-5 text-brand-600" />
                </div>
                <h3 className="mt-4 text-lg font-bold">{useCase.title}</h3>
                <p className="mt-2 text-slate-600">{useCase.description}</p>
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <p className="text-sm font-semibold text-green-600">{useCase.result}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Moments Section */}
      <section className="bg-white px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl">
                Why choose Moments by Zuri?
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                Built for businesses that want to reach customers without the complexity and cost of enterprise tools.
              </p>

              <div className="mt-8 space-y-6">
                <div className="flex gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100 flex-shrink-0">
                    <Shield className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-bold">Official WhatsApp Partner</h3>
                    <p className="mt-1 text-slate-600">Direct integration with Meta's WhatsApp Business API for reliable delivery.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 flex-shrink-0">
                    <CreditCard className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-bold">Pay-As-You-Go Pricing</h3>
                    <p className="mt-1 text-slate-600">No monthly subscriptions. Only pay for the messages you actually send.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100 flex-shrink-0">
                    <BarChart3 className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-bold">Unified Analytics</h3>
                    <p className="mt-1 text-slate-600">Track performance across all channels from one dashboard.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100 flex-shrink-0">
                    <Clock className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-bold">Easy Scheduling</h3>
                    <p className="mt-1 text-slate-600">Schedule campaigns in advance and let them run automatically.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-100 flex-shrink-0">
                    <Globe className="h-5 w-5 text-brand-600" />
                  </div>
                  <div>
                    <h3 className="font-bold">Global Reach</h3>
                    <p className="mt-1 text-slate-600">Send messages to customers in 190+ countries with reliable delivery.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-tr from-brand-500/20 to-purple-500/20 blur-3xl" />
              <div className="relative rounded-2xl bg-slate-900 p-8 text-white">
                <h3 className="text-2xl font-bold">Start sending in minutes</h3>
                <p className="mt-2 text-slate-300">
                  No complex setup. No long contracts. Just create your account and start reaching customers.
                </p>
                <ul className="mt-6 space-y-3">
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-500" />
                    <span>Free account setup</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-500" />
                    <span>No credit card required</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-500" />
                    <span>Pay only for what you send</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-500" />
                    <span>Cancel anytime</span>
                  </li>
                </ul>
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-8 inline-flex items-center justify-center gap-2 rounded-lg bg-brand-600 px-6 py-3 font-semibold text-white hover:bg-brand-500 transition-colors"
                >
                  Get Started Free
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="bg-slate-50 px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl">
              Trusted by businesses across industries
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              From e-commerce to healthcare, Moments helps businesses across all industries connect with customers and drive results.
            </p>
          </div>

          <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {industries.map((industry, index) => (
              <div key={index} className="rounded-2xl bg-white p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100">
                  <industry.icon className="h-6 w-6 text-slate-700" />
                </div>
                <h3 className="mt-4 text-lg font-bold">{industry.name}</h3>
                <p className="mt-2 text-slate-600">{industry.description}</p>
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <p className="text-sm font-semibold text-brand-600">{industry.stat}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Stats Section */}
      <section className="bg-white px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl">
              Proven results you can count on
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Businesses using Moments see measurable improvements in customer engagement and conversion rates.
            </p>
          </div>

          <div className="mt-16 grid gap-8 md:grid-cols-4">
            <div className="text-center">
              <p className="text-4xl font-extrabold text-brand-600">98%</p>
              <p className="mt-2 text-slate-600">SMS delivery rate</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-extrabold text-brand-600">45%</p>
              <p className="mt-2 text-slate-600">Higher open rates than email</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-extrabold text-brand-600">3x</p>
              <p className="mt-2 text-slate-600">Faster response times</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-extrabold text-brand-600">190+</p>
              <p className="mt-2 text-slate-600">Countries covered</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="bg-white px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl">
              Pay only for what you send
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              No monthly subscriptions. No hidden fees. No minimum commitments. Just pure pay-as-you-go pricing.
            </p>
          </div>

          <div className="mt-16 grid gap-8 md:grid-cols-3">
            {pricing.map((plan, index) => (
              <div key={index} className="rounded-2xl border border-slate-200 p-8 hover:border-brand-500 transition-colors">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100">
                  <plan.icon className="h-6 w-6 text-slate-700" />
                </div>
                <h3 className="mt-4 text-xl font-bold">{plan.channel}</h3>
                <p className="mt-2 text-slate-600">{plan.description}</p>
                <div className="mt-6">
                  <p className="text-2xl font-bold text-slate-900">{plan.startingFrom}</p>
                  <p className="text-sm text-slate-500 mt-1">Pay per message sent</p>
                </div>
                <ul className="mt-6 space-y-3">
                  {plan.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-2 text-sm text-slate-600">
                      <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-8 block w-full rounded-lg border border-slate-300 px-6 py-3 text-center font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Get Started
                </a>
              </div>
            ))}
          </div>

          <div className="mt-12 rounded-2xl bg-brand-50 p-8 text-center">
            <h3 className="text-xl font-bold text-brand-900">Volume discounts available</h3>
            <p className="mt-2 text-brand-700">
              Send more, pay less. Contact us for custom pricing on high-volume campaigns. Enterprise solutions available.
            </p>
            <a
              href={site.workspaceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center justify-center gap-2 rounded-lg bg-brand-600 px-6 py-3 font-semibold text-white hover:bg-brand-500 transition-colors"
            >
              Contact Sales
              <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-slate-50 px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl">
              Frequently asked questions
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Everything you need to know about ZuriHub Moments.
            </p>
          </div>

          <div className="mt-12 space-y-4">
            {momentsFaqs.map((faq, index) => (
              <details key={index} className="group rounded-2xl bg-white border border-slate-200">
                <summary className="flex cursor-pointer items-center justify-between p-6 font-semibold text-slate-900">
                  {faq.q}
                  <ArrowDown className="h-5 w-5 text-slate-500 transition-transform group-open:rotate-180" />
                </summary>
                <p className="px-6 pb-6 text-slate-600">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-slate-950 px-4 py-24 text-white sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl">
            Ready to reach your customers?
          </h2>
          <p className="mt-4 text-lg text-slate-300">
            Start sending WhatsApp Marketing, email campaigns, and bulk SMS today. No credit card required.
          </p>
          <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:justify-center">
            <a
              href={site.workspaceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-600 px-8 py-4 font-semibold text-white hover:bg-brand-500 transition-colors"
            >
              Start Free
              <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="/product"
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-600 px-8 py-4 font-semibold text-white hover:bg-slate-800 transition-colors"
            >
              Explore All Products
              <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
